import React, { useState } from 'react';
import axios from 'axios';

const EmiCalculator = () => {
  const [calcType, setCalcType] = useState('emi');
  const [loanType, setLoanType] = useState('');
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('');
  const [periodYears, setPeriodYears] = useState('');
  const [periodMonths, setPeriodMonths] = useState('');
  const [emi, setEmi] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);

  const darkBlue = '#1a1a5c';

  const loanPresets = {
    home: { rate: 8.5, tenureYears: 20 },
    personal: { rate: 12.5, tenureYears: 5 },
    education: { rate: 9.0, tenureYears: 8 },
    car: { rate: 9.5, tenureYears: 7 },
    business: { rate: 13.5, tenureYears: 10 }
  };

  const getTotalMonths = () => {
    const years = parseInt(periodYears) || 0;
    const months = parseInt(periodMonths) || 0;
    return years * 12 + months;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      calc_type: calcType,
      principal: parseFloat(amount) || 0,
      rate: parseFloat(rate) || 0,
      period: getTotalMonths(),
      emi: emi ? parseFloat(emi) : 0,
      loan_type: loanType
    };

    console.log("📤 Payload sent:", payload);

    try {
      const response = await axios.post('http://localhost:8000/api/loan/calculate/', payload);
      console.log("✅ API Response:", response.data);

      setResult(response.data.result);
      setDetails(response.data.details || null);
      setError(null);
    } catch (err) {
      console.error("❌ API Error:", err.response?.data || err.message);
      setResult(null);
      setDetails(null);
      setError(err.response?.data?.error || "Something went wrong. Please check your inputs and try again.");
    }
  };

  return (
    <div style={{ backgroundColor: '#f1f6ff', minHeight: '100vh', padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      <div style={{
        maxWidth: '500px',
        margin: 'auto',
        background: 'white',
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ textAlign: 'center', color: darkBlue, fontSize: '24px', fontWeight: '700', marginBottom: '1rem' }}>
          Loan Calculator
        </h2>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '2rem' }}>
          {['emi', 'principal', 'period', 'interest_rate'].map(type => (
            <button
              key={type}
              onClick={() => {
                setCalcType(type);
                setResult(null);
                setDetails(null);
              }}
              style={{
                padding: '10px 24px',
                borderRadius: '25px',
                border: `2px solid ${darkBlue}`,
                backgroundColor: calcType === type ? darkBlue : 'white',
                color: calcType === type ? 'white' : darkBlue,
                fontWeight: '600',
                fontSize: '16px',
                cursor: 'pointer',
                boxShadow: calcType === type ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.3s ease'
              }}
            >
              {type === 'emi' ? 'EMI' : type === 'principal' ? 'Principal' : type === 'period' ? 'Time' : 'Interest Rate'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle(darkBlue)}>Loan Type</label>
          <select
            value={loanType}
            onChange={(e) => {
              const selected = e.target.value;
              setLoanType(selected);
              setResult(null);
              setDetails(null);

              if (loanPresets[selected]) {
                setRate(loanPresets[selected].rate.toString());
                setPeriodYears(loanPresets[selected].tenureYears.toString());
                setPeriodMonths("0");
              }
            }}
            style={{
              ...inputStyle(darkBlue),
              appearance: 'none',
              backgroundColor: 'white',
              fontWeight: '500'
            }}
          >
            <option value="">Select Loan Type</option>
            {Object.keys(loanPresets).map(key => (
              <option key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)} Loan
              </option>
            ))}
          </select>

          {loanType && loanPresets[loanType] && (
            <div style={{
              backgroundColor: '#f5faff',
              border: `1px solid ${darkBlue}`,
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              fontSize: '14px',
              color: darkBlue
            }}>
              <strong>Selected: {loanType.charAt(0).toUpperCase() + loanType.slice(1)} Loan</strong><br />
              Suggested Interest Rate: <strong>{loanPresets[loanType].rate}%</strong><br />
              Suggested Tenure: <strong>{loanPresets[loanType].tenureYears} years</strong>
            </div>
          )}

          {(calcType === 'emi' || calcType === 'period' || calcType === 'interest_rate') && (
            <>
              <label style={labelStyle(darkBlue)}>Loan Amount</label>
              <input
                type="number"
                placeholder="e.g. 500000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={inputStyle(darkBlue)}
              />
            </>
          )}

          {(calcType === 'emi' || calcType === 'principal' || calcType === 'period') && (
            <>
              <label style={labelStyle(darkBlue)}>Interest Rate (Annual %)</label>
              <input
                type="number"
                placeholder="e.g. 9.5"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                style={inputStyle(darkBlue)}
              />
            </>
          )}

          {(calcType === 'principal' || calcType === 'interest_rate' || calcType === 'period') && (
            <>
              <label style={labelStyle(darkBlue)}>EMI Amount</label>
              <input
                type="number"
                placeholder="e.g. 10000"
                value={emi}
                onChange={(e) => setEmi(e.target.value)}
                style={inputStyle(darkBlue)}
              />
            </>
          )}

          {(calcType === 'emi' || calcType === 'principal' || calcType === 'interest_rate') && (
            <>
              <label style={labelStyle(darkBlue)}>Loan Tenure</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <input
                  type="number"
                  placeholder="Years"
                  value={periodYears}
                  onChange={(e) => setPeriodYears(e.target.value)}
                  style={inputStyle(darkBlue)}
                />
                <input
                  type="number"
                  placeholder="Months"
                  value={periodMonths}
                  onChange={(e) => setPeriodMonths(e.target.value)}
                  style={inputStyle(darkBlue)}
                />
              </div>
            </>
          )}

          <button type="submit" style={{
            width: '100%',
            padding: '12px',
            backgroundColor: darkBlue,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            marginTop: '1rem',
            fontWeight: 'bold',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease'
          }}>
            Calculate
          </button>
        </form>

        {result !== null && (
          <div style={{
            marginTop: '1.5rem',
            backgroundColor: '#eafcf1',
            padding: '1rem',
            borderRadius: '12px',
            border: '1px solid #2ecc71',
            color: '#2c7c47',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>
            {calcType === 'emi' && (
              <>
                <p>Your Monthly EMI: ₹ {Number(result).toLocaleString()}</p>
                {details && (
                  <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                    <li>Total Interest: ₹ {details.total_interest.toLocaleString()}</li>
                    <li>Total Payment: ₹ {details.total_payment.toLocaleString()}</li>
                    <li>Total Tenure: {details.total_months} months</li>
                  </ul>
                )}
              </>
            )}
            {calcType === 'principal' && <>Principal Amount is: ₹ {Number(result).toLocaleString()}</>}
            {calcType === 'period' && (
              <>
                Estimated Tenure:&nbsp;
                {Math.floor(result / 12)} {Math.floor(result / 12) === 1 ? 'Year' : 'Years'}&nbsp;
                {Math.round(result % 12)} {Math.round(result % 12) === 1 ? 'Month' : 'Months'}
              </>
            )}
            {calcType === 'interest_rate' && <>Estimated Annual Interest Rate: {result}%</>}
          </div>
        )}

        {error && (
          <p style={{ color: 'red', marginTop: '1rem' }}>{error}</p>
        )}
      </div>
    </div>
  );
};

const inputStyle = (borderColor) => ({
  width: '100%',
  padding: '10px',
  borderRadius: '8px',
  border: `1px solid ${borderColor}`,
  fontSize: '16px',
  outline: 'none',
  marginBottom: '1rem'
});

const labelStyle = (color) => ({
  color: color,
  fontWeight: '600',
  marginBottom: '0.25rem',
  display: 'block'
});

export default EmiCalculator;




